import React, { useCallback, useState } from 'react';
import Form from '@ff/ui/Form2';
import AuthFormFields, { initialValues } from '@ff/ui/Form2/presets/AuthForm';
import useLogin from 'client/hooks/mutations/useLogin';
import { getGaCookies } from 'client/utils/getCookies';
import handleGraphQlError from 'client/utils/handleGraphQLErrors';
import useRegister from 'client/hooks/mutations/useRegister';
import useIsUserExist from 'client/hooks/mutations/useIsUserExist';
import useSendEmailCode from 'client/hooks/mutations/useSendEmailCode';
import url from 'client/utils/url';
import PATHS from 'client/utils/paths';
import debounce from 'lodash/debounce';

export default function AuthForm() {

  const [userExist, setUserExist] = useState<boolean | null>(null);

  const [login] = useLogin();
  const [register] = useRegister();
  const [isUserExist] = useIsUserExist();
  const [sendEmailCode] = useSendEmailCode();

  const handleLogin = async (data, form) => {
    const { email, password } = data;

    try {
      await login({
        variables: {
          email,
          password,
          gaCookies: getGaCookies()
        }
      });
    } catch (error) {
      const errors = handleGraphQlError(error);

      form.setSubmitError('login', errors[0]);
    }
  }

  const handleRegister = async (data, form) => {
    try {
      const { email, newPassword, verificationCode } = data;

      await register({
        variables: {
          registerForm: {
            email,
            password: newPassword,
            code: verificationCode
          }
        }
      });
    } catch (error) {
      const errors = handleGraphQlError(error);

      form.setSubmitError('register', errors[0]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleEmailChange = useCallback(
    debounce(async (email: string) => {
      try {
        const { data } = await isUserExist({ variables: { email } });

        setUserExist(!!data?.isUserExist);
      } catch (error) {
        const errors = handleGraphQlError(error);

        console.error('failed to check user', errors[0]);
      }
    }, 500, { leading: false, trailing: true })
    , []);

  const handleSendEmailCode = async (email) => {
    try {
      await sendEmailCode({
        variables: {
          email
        }
      });
    } catch (error) {
      const errors = handleGraphQlError(error);

      console.error('failed to send email code', errors[0]);
    }
  }

  const handleSubmit = async (data, form) => {
    if (userExist) {
      await handleLogin(data, form);
    } else {
      await handleRegister(data, form);
    }
  }

  return (
    <Form
      formId='auth'
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <AuthFormFields
        linkModal
        userExist={userExist}
        privacyPolicyLink={`${url.getSameRegionPath(PATHS.PRIVACY)}/?APPONLY=1`}
        termsLink={`${url.getSameRegionPath(PATHS.TERMS_OF_USE)}/?APPONLY=1`}
        forgotPasswordLink={url.getSameRegionPath(PATHS.FORGOT_PASS)}
        onValidEmailChange={debouncedHandleEmailChange}
        onVerifyClick={handleSendEmailCode}
      />
    </Form>
  );
}
